import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Chip,
  Button,
  CircularProgress,
  LinearProgress,
  Alert,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { useFamily } from '../../context/FamilyContext';
import { useLearnerMastery } from '../../hooks/useLearnerMastery';
import { useSkillGraph } from '../../hooks/useSkillGraph';
import { useNextSkills } from '../../hooks/useNextSkills';
import { effectiveStatus, ParentOverride, MasteryStatus } from '../../utils/masteryStatus';
import OverrideControl from './OverrideControl';

// Status vocabulary is deliberately under-claiming - the stronger
// claim-of-completion word is banned from this vocabulary entirely.
// See IMPLEMENTATION_PLAN.md §8.2 / IMPLEMENTATION_STATE.md Phase 2.
const STATUS_LABEL: Record<string, string> = {
  'not-attempted': 'Not started yet',
  emerging: 'Just getting started',
  developing: 'Making progress',
  secure: 'Looks solid',
};

const STATUS_COLOR: Record<string, 'default' | 'warning' | 'info' | 'success'> = {
  'not-attempted': 'default',
  emerging: 'warning',
  developing: 'info',
  secure: 'success',
};

interface SkillCardData {
  skillId: string;
  code: string;
  name: string;
  status: MasteryStatus;
  confidence: number;
  evidenceCount: number;
  correctCount: number;
  parentOverride?: ParentOverride | null;
}

const MasteryMap: React.FC = () => {
  const navigate = useNavigate();
  const { learners, loading: familyLoading } = useFamily();
  // Multi-child UI is out of scope for Phase 4 - the schema supports several
  // children, this view only ever shows the first one.
  const learner = learners[0] || null;

  const { mastery, loading: masteryLoading, error: masteryError, refetch: refetchMastery } = useLearnerMastery(learner?._id || null);
  const { nodes: skillNodes, loading: graphLoading } = useSkillGraph();
  const { recommendations, needsCheck, loading: nextLoading } = useNextSkills(learner?._id || null, { subject: 'math', limit: 3 });

  const loading = familyLoading || masteryLoading || graphLoading;

  // Dismissible, not a wall: a parent can close this and still see the (empty)
  // skill map, and can always come back to placement via the Assessments nav
  // entry later. Keyed per-learner so dismissing for one child doesn't hide it
  // for a sibling. learner arrives async (useFamily loads after mount), so
  // this re-syncs from localStorage once the real id is known rather than
  // baking a premature `false` into useState's lazy initializer.
  const dismissKey = learner ? `placementPromptDismissed:${learner._id}` : null;
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    setDismissed(Boolean(dismissKey && localStorage.getItem(dismissKey)));
  }, [dismissKey]);
  const dismissPlacementPrompt = () => {
    if (dismissKey) localStorage.setItem(dismissKey, '1');
    setDismissed(true);
  };

  // Every skill in the graph gets a card, even with zero evidence -
  // "absence means not-attempted" (Phase 2 convention), not "missing data".
  const skillCards: SkillCardData[] = useMemo(() => {
    const masteryBySkillId = new Map(mastery.map((m) => [m.skillId._id, m]));
    return skillNodes.map((node) => {
      const m = masteryBySkillId.get(node.id);
      return {
        skillId: node.id,
        code: node.code,
        name: node.name,
        status: effectiveStatus(m),
        confidence: m?.confidence || 0,
        evidenceCount: m?.evidenceCount || 0,
        correctCount: m?.correctCount || 0,
        parentOverride: m?.parentOverride,
      };
    });
  }, [mastery, skillNodes]);

  const hasAnyEvidence = mastery.length > 0;

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!learner) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Add a child to get started
          </Typography>
          <Button variant="contained" onClick={() => navigate('/onboarding')}>
            Add a Child
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            {learner.firstName}'s Skill Map
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Math &middot; Grade {learner.gradeLevel}
          </Typography>
        </Box>
        <Button variant="outlined" onClick={() => navigate('/dashboard/progress')}>
          Weekly progress
        </Button>
      </Box>
      <Box sx={{ mb: 3 }} />

      {masteryError && <Alert severity="warning" sx={{ mb: 3 }}>{masteryError}</Alert>}

      {!hasAnyEvidence && !dismissed && (
        <Paper sx={{ p: 3, mb: 4, position: 'relative' }}>
          <IconButton
            size="small"
            onClick={dismissPlacementPrompt}
            sx={{ position: 'absolute', top: 8, right: 8 }}
            aria-label="Dismiss"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
            Let's find out where {learner.firstName} is
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            A quick placement check (about 15-20 minutes) shows which math skills
            look solid and which ones to work on next. This isn't a test with a
            score - just a starting point. You can always start one later from
            Assessments in the nav.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate(`/dashboard/placement/${learner._id}?subject=math`)}
          >
            Start Placement Check
          </Button>
        </Paper>
      )}

      {!nextLoading && needsCheck.length > 0 && (
        <Paper sx={{ p: 3, mb: 4, backgroundColor: 'warning.50' }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Quick checks that would help
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            These aren't recommended yet only because a prerequisite has never been
            checked - not because {learner.firstName} is struggling with it.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {needsCheck.map((item) => (
              <Box
                key={item.skillId}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  p: 2,
                  backgroundColor: 'background.paper',
                  borderRadius: 1,
                }}
              >
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {item.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Blocked by: {item.blockedBy.map((b) => b.name).join(', ')}
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() =>
                    navigate(`/dashboard/skills/${item.blockedBy[0].skillId}?learnerId=${learner._id}`)
                  }
                >
                  Check now
                </Button>
              </Box>
            ))}
          </Box>
        </Paper>
      )}

      {!nextLoading && recommendations.length > 0 && (
        <Paper sx={{ p: 3, mb: 4, backgroundColor: 'primary.50' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            What's next
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {recommendations.map((rec) => (
              <Box
                key={rec.skillId}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  p: 2,
                  backgroundColor: 'background.paper',
                  borderRadius: 1,
                }}
              >
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {rec.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {rec.reason}
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => navigate(`/dashboard/skills/${rec.skillId}?learnerId=${learner._id}`)}
                >
                  Work on this
                </Button>
              </Box>
            ))}
          </Box>
        </Paper>
      )}

      <Typography variant="h6" sx={{ mb: 2 }}>
        All skills
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
          gap: 2,
        }}
      >
        {skillCards.map((card) => (
          <Paper
            key={card.skillId}
            sx={{
              p: 2.5,
              cursor: 'pointer',
              transition: 'box-shadow 0.15s ease-in-out',
              '&:hover': { boxShadow: 4 },
            }}
            onClick={() => navigate(`/dashboard/skills/${card.skillId}?learnerId=${learner._id}`)}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1, pr: 1 }}>
                {card.name}
              </Typography>
              <Chip
                label={STATUS_LABEL[card.status]}
                color={STATUS_COLOR[card.status]}
                size="small"
              />
            </Box>

            {/* Every mastery state shown must display confidence + evidence
                count alongside it - a parent accepts "7 of 8 items right
                across two sessions", not an unexplained verdict. */}
            {card.evidenceCount > 0 ? (
              <>
                <LinearProgress
                  variant="determinate"
                  value={Math.round(card.confidence * 100)}
                  sx={{ mb: 1, height: 6, borderRadius: 3 }}
                />
                <Typography variant="caption" color="text.secondary">
                  {card.correctCount} of {card.evidenceCount} correct ({Math.round(card.confidence * 100)}% confidence)
                </Typography>
              </>
            ) : (
              <Typography variant="caption" color="text.secondary">
                No attempts yet
              </Typography>
            )}

            {card.parentOverride && (
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }} color="text.secondary">
                You set this status
              </Typography>
            )}

            <Box sx={{ mt: 1 }} onClick={(e) => e.stopPropagation()}>
              <OverrideControl
                learnerId={learner._id}
                skillId={card.skillId}
                currentEffectiveStatus={card.status}
                existingOverride={card.parentOverride}
                onOverridden={refetchMastery}
              />
            </Box>
          </Paper>
        ))}
      </Box>
    </Container>
  );
};

export default MasteryMap;
